import React from 'react';

export const BlogCustomFilter = (props) => {
    return (
        <div className='blog-con'>
            <hr />
            <article>
                <section>
                    <p>
                    In this blog post, we will focus on how to create and configure a custom servlet filter in Liferay using the @Component annotation. Servlet filters are useful for intercepting HTTP requests and responses, allowing you to add additional processing before or after the request reaches the target servlet.
                    </p>
                    <h2>Objective of Servlet Filter</h2>
                    <p>
                        A <strong>Servlet Filter</strong> is an object that intercepts HTTP requests to perform pre-processing tasks (before the request reaches the backend) or post-processing tasks (before the response reaches the client). Filters are often used for operations such as logging, authentication, and modifying request/response headers.
                    </p>
                    <p>
                        Filters can be useful when you need to perform certain tasks before the request reaches the backend, such as checking user credentials, logging request data, or modifying request headers. Similarly, they can also be used to modify responses or add additional information before sending the response to the client.
                    </p>
                    <h2>When to Use Servlet Filters</h2>
                    <p>
                        You might need to use a servlet filter when you want to apply functionality to multiple servlets without the servlets themselves needing to know about it. Some common use cases for servlet filters include:
                    </p>
                    <ul>
                        <li><strong>Logging</strong>: Record details of incoming requests, such as request headers, URL paths, or query parameters.</li>
                        <li><strong>Auditing</strong>: Track and log user activities, like login attempts, access to sensitive resources, etc.</li>
                        <li><strong>Transaction management</strong>: Manage database transactions across multiple servlets or requests.</li>
                        <li><strong>Security</strong>: Implement security features like authentication, authorization, or input validation before passing the request to the target servlet.</li>
                    </ul>
                    <p>
                        Servlet filters can be applied in a chain, where one filter processes the request first and passes it to the next. By mapping filters to specific URLs, you ensure that the filters only apply to requests that match those patterns.
                    </p>
                    <p>
                        Some typical types of filters you might encounter or create include:
                    </p>
                    <ul>
                        <li><strong>Authentication Filters</strong>: Ensures that the user is authenticated before proceeding with the request.</li>
                        <li><strong>Compression Filters</strong>: Compresses responses to improve performance and reduce bandwidth usage.</li>
                        <li><strong>Encryption Filters</strong>: Encrypts request or response data for security purposes.</li>
                        <li><strong>Image Compression Filters</strong>: Reduces the size of images to improve loading times.</li>
                        <li><strong>Tokenizing Filters</strong>: Splits or tokenizes data as it is processed through the filter.</li>
                    </ul>
                    <p>
                        Filters are a powerful tool for modifying both request and response objects, and can be configured to apply only to specific URLs or servlets, ensuring flexibility and scalability in web applications.
                    </p>
                </section>

                <section>
                    <h2>Creating Your Custom Servlet Filter</h2>
                    <h3>Code Example</h3>
                    <pre><code>
{`@Component(
    immediate = true, 
    property = {
        "before-filter=Absolute Redirects Filter",
        "servlet-context-name=", 
        "servlet-filter-name=Custom Filter",
        "url-pattern=/*" 
    }, 
    service = Filter.class
)
public class CustomFilterServlet extends BaseFilter {

    private static final Log _log = LogFactoryUtil.getLog(CustomFilterServlet.class);

    @Override
    protected void processFilter(HttpServletRequest request, HttpServletResponse response, FilterChain filterChain)
            throws Exception {
        HttpServletRequest httpRequest = (HttpServletRequest) request;

        // Example of reading query string from the request
        System.out.println(httpRequest.getQueryString());

        // Here you could process other parameters as well
        // long someParameter = ParamUtil.getLong(request, "someParameter");

        _log.info("Filter is invoked");

        // Continue the filter chain, allowing other filters or servlets to process the request
        super.processFilter(request, response, filterChain);
    }

    @Override
    protected Log getLog() {
        return _log;
    }
}
`}</code></pre>
                        <strong>Explanation of the Code:</strong>
                        <ul>
                            <li><strong>Component Annotation</strong>: 
                                <ul>
                                    <li><code>@Component</code>: This annotation registers the filter as a service in Liferay.</li>
                                    <li><code>immediate = true</code>: Ensures the filter is loaded immediately when the application starts.</li>
                                    <li><code>property</code>: Specifies several properties for the filter.</li>
                                    <li><code>service = Filter.class</code>: Indicates that this class is a filter.</li>
                                </ul>
                            </li>
                            <li><strong>processFilter Method</strong>: 
                                <ul>
                                    <li>This method intercepts the incoming request and response. It allows you to perform any processing before the request reaches the target servlet.</li>
                                    <li>In the example, we print the query string from the request using <code>httpRequest.getQueryString()</code>.</li>
                                    <li>After performing any custom logic, we call <code>super.processFilter(request, response, filterChain)</code> to continue the filter chain.</li>
                                    <li>We can also redirect the URL if needed using <code>httpServletResponse.sendRedirect("https://www.redirecturl.com")</code>.</li>
                                </ul>
                            </li>
                            <li><strong>Logging</strong>: 
                                <ul>
                                    <li>We use Liferay's <code>LogFactoryUtil</code> to log when the filter is invoked. This helps to track when and how the filter is being triggered.</li>
                                </ul>
                            </li>
                        </ul>
                </section>

                <section>
                    <h2>Component Configuration</h2>
                    
                        The <code>@Component</code> annotation configures your filter and specifies its execution context. It defines:
                        <ul>
                            <li>The order and scope of the filter.</li>
                            <li>The URL pattern that the filter should apply to (in this case, all URLs).</li>
                            <li>The filter’s name and context for identification within the application.</li>
                        </ul>
                    
                    <h3>Key Properties in the <code>@Component</code> Annotation:</h3>
                    <ul>    
                        <li><strong>before-filter</strong> and <strong>after-filter:</strong> The filter that should be run before the custom filter you're creating and the filter that should be run after, respectively (Not required). To learn more about filters, check <a target='_blank' rel="noreferrer" href="https://github.com/liferay/liferay-portal/blob/master/portal-web/docroot/WEB-INF/liferay-web.xml">Liferay-web.xml</a>.</li>
                        <li><strong>servlet-context-name</strong>: This should always be set to an empty string.</li>
                        <li><strong>servlet-filter-name</strong>: The name of your filter, in this case, it’s <code>"Custom Filter"</code>.</li>
                        <li><strong>url-pattern</strong>: The URL pattern to match, such as <code>"/*"</code> to apply the filter to all URLs. You can adjust this to target specific paths.</li>
                    </ul>
                    <p>
                        By setting up your filter like this, you ensure that the filter is invoked whenever any request matches the URL pattern defined in <code>url-pattern</code>.
                    </p>
                </section>

                <footer>
                    <p><strong>Conclusion:</strong></p>
                    <p>
                        Creating and configuring a servlet filter in Liferay involves extending the <code>BaseFilter</code> class and using the <code>@Component</code> annotation for configuration. By following these steps, you can intercept HTTP requests and responses, log information, and implement custom logic before forwarding the request to the next filter or servlet.
                    </p>
                    <p>
                        In this example, we focused on logging the query string of the request, but you can modify the <code>processFilter</code> method to perform other tasks such as parameter extraction, authentication, or even modifying the response before it reaches the client. The flexibility of filters makes them a powerful tool in web application development.
                    </p>
                    <p>Happy coding! &#128521;</p>
                </footer>
            </article>
        </div>
    );
};
